import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProjectTask } from '@/types/odoo';
import { usePlanningStore } from '@/stores/planning';
import { getWeekDates, getFeestdagDates, groupTasksFlatInstallers, isFeestdagMarker, formatDateKey } from '@/utils/planning';
import { applyInstallerRowOrder } from '@/utils/planningRowOrder';
import { weekOffsetForDate } from '@/views/PlanningView';
import { PlanningGrid, type TrayDropPayload } from '@/components/planning/PlanningGrid';
import { updatePlanningTask, scheduleFase, fetchArchivedTasks, type ArchivedTask } from '@/api/planning';
import { InplannenTray } from './InplannenTray';
import { TaskDetailPopup } from './TaskDetailPopup';

/**
 * Inline "Inplannen" scheduler — the standard per-fase planning view inside the
 * dashboard's Planning section (no modal). Hosts the REAL planning board; the
 * project search (default = current project) keeps it focused, but ALL visible
 * cards are editable/draggable by design (admin-only surface — cross-project edits
 * are intended). A "Nog in te plannen" tray places undated tasks / a new fase task
 * and lists "Verwijderde taken" (archived) that can be dragged back onto a day to
 * un-archive them; each card has a detail button → description + documenten popup.
 * Removing a card (trash) archives it (active=false), same as the planning tab.
 *
 * It drives the GLOBAL usePlanningStore. It only resets transient board *modes* on
 * open (so cards are draggable); it deliberately does NOT touch the user's
 * projectFilter / showAllInstallers / hideCompleted (uses its own local search +
 * always-show-all), so the full Planning board's prefs aren't clobbered. Edits are
 * flushed to the dashboard detail via onScheduled (after each schedule / move).
 */
interface InplannenPanelProps {
  projectId: number;
  projectName: string;
  faseId: number | null;
  faseName: string;
  /** Active-fase undated tasks (from the dashboard's detail.projectTasks). */
  unplannedTasks: ProjectTask[];
  /** All of this project's tasks (with descriptions) — opens the detail popup on click. */
  projectTasks: ProjectTask[];
  /** Installer id → name, for the task popup meta. */
  installerNames: Record<string, string>;
  /** yyyy-mm-dd week to open on (the fase's start week). */
  initialWeekDateKey: string;
  /** Refresh the dashboard detail (tray + fase status) after edits / on leave. */
  onScheduled: () => void;
  /** Read-only viewers (no planning:write): show the week board but suppress every
   *  write vector — no drag tray, no archived-recover fetch. Card moves are already
   *  blocked inside PlanningGrid for anyone without the planning_edit feature. */
  readOnly?: boolean;
}

const fmt = (d: Date): string => {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}`;
};

export function InplannenPanel({
  projectId,
  projectName,
  faseId,
  faseName,
  unplannedTasks,
  initialWeekDateKey,
  projectTasks,
  installerNames,
  onScheduled,
  readOnly = false,
}: InplannenPanelProps) {
  const tasks = usePlanningStore((s) => s.tasks);
  const userMap = usePlanningStore((s) => s.userMap);
  const employeeMap = usePlanningStore((s) => s.employeeMap);
  const easykitConfig = usePlanningStore((s) => s.easykitConfig);
  const weekOffset = usePlanningStore((s) => s.weekOffset);
  const installerRowOrder = usePlanningStore((s) => s.installerRowOrder);
  const collapsedGroups = usePlanningStore((s) => s.collapsedGroups);
  const toggleGroup = usePlanningStore((s) => s.toggleGroup);
  const loading = usePlanningStore((s) => s.loading);
  const error = usePlanningStore((s) => s.error);

  const projectNameMap = usePlanningStore((s) => s.projectNameMap);
  const navigate = useNavigate();

  // Optimistically hide tasks once placed (the dashboard detail refetch confirms).
  const [scheduledTaskIds, setScheduledTaskIds] = useState<Set<number>>(() => new Set());
  // Project filter — defaults to the current project; clear it to see all projects.
  const [search, setSearch] = useState(projectName);
  // Task detail/description popup (opened by clicking a card).
  const [popupTask, setPopupTask] = useState<ProjectTask | null>(null);
  // Archived ("Verwijderde taken") tasks for this project (filtered to fase below).
  const [archived, setArchived] = useState<ArchivedTask[]>([]);

  // On open (keyed by fase → this re-runs per fase): clear any leftover full-board
  // transient state, show every project, jump to the fase's week and fetch. There is
  // deliberately NO snapshot/restore on unmount — restoring weekOffset flashed the old
  // week on every fase switch / StrictMode remount, and PlanningView re-seeds itself
  // on its own mount, so nothing needs restoring here.
  useEffect(() => {
    const s = usePlanningStore.getState();
    // Only reset transient board MODES (so this project's cards are draggable here);
    // do NOT touch projectFilter / showAllInstallers / hideCompleted — those are the
    // full Planning board's user prefs and the panel doesn't read them.
    usePlanningStore.setState({
      createMode: null,
      scheduleProjectMode: null,
      scheduleTargetCell: null,
      cellContextMenu: null,
      installerEditor: null,
      error: null,
    });
    const offset = weekOffsetForDate(initialWeekDateKey);
    s.setWeekOffset(offset);
    void s.fetchPlanning(offset);
    // "Verwijderde taken" is a write/recover surface — skip it for read-only viewers.
    if (!readOnly) void refetchArchived();
    if (!s.installerRowOrderHydrated) void s.hydrateInstallerRowOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per open
  }, []);

  const weekData = getWeekDates(weekOffset);
  // Filter to the searched project (default = current project). Day-markers stay.
  const filteredTasks = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return tasks;
    return tasks.filter((task) => {
      if (isFeestdagMarker(task)) return true;
      const soId = Array.isArray(task.sale_order_id) ? task.sale_order_id[0] : null;
      const pName = soId ? projectNameMap.get(soId) : null;
      if (pName && pName.toLowerCase().includes(q)) return true;
      if (task.project_id && String(task.project_id[1]).toLowerCase().includes(q)) return true;
      if (task.name && task.name.toLowerCase().includes(q)) return true;
      const soName = Array.isArray(task.sale_order_id) ? task.sale_order_id[1] : null;
      if (soName && String(soName).toLowerCase().includes(q)) return true;
      return false;
    });
  }, [tasks, search, projectNameMap]);
  const flatRows = groupTasksFlatInstallers(filteredTasks, weekData.days, userMap, true, employeeMap, easykitConfig);
  const orderedFlatRows = applyInstallerRowOrder(flatRows, installerRowOrder);
  const feestdagDates = getFeestdagDates(filteredTasks);
  const visibleUnplanned = unplannedTasks.filter((t) => !scheduledTaskIds.has(t.id));
  // Archived tasks belonging to the active fase (drag back onto a day to recover).
  const faseArchived = useMemo(
    () => (faseId == null ? [] : archived.filter((a) => Array.isArray(a.parent_id) && a.parent_id[0] === faseId)),
    [archived, faseId],
  );

  function changeWeek(delta: number) {
    const s = usePlanningStore.getState();
    const next = s.weekOffset + delta;
    s.setWeekOffset(next);
    void s.fetchPlanning(next);
  }

  async function refetchArchived() {
    try {
      const res = await fetchArchivedTasks(projectId);
      setArchived(res.tasks);
    } catch {
      /* non-fatal: keep the previous list */
    }
  }

  // A card mutated the board (reschedule or unschedule→archive): refresh the dashboard
  // detail AND re-pull "Verwijderde taken" so a just-deleted task appears there.
  function handleBoardChanged() {
    void refetchArchived();
    onScheduled();
  }

  async function handleTrayDrop(payload: TrayDropPayload, target: { dateKey: string; installerId: number }) {
    const installerIds = target.installerId > 0 ? [target.installerId] : [];
    try {
      if (payload.kind === 'unplanned-task') {
        // Backend sets planned_date_begin (07:00) + date_deadline (16:00) from this date.
        await updatePlanningTask(payload.taskId, {
          date_deadline: target.dateKey,
          x_studio_installateur: installerIds,
        });
        setScheduledTaskIds((prev) => {
          const n = new Set(prev);
          n.add(payload.taskId);
          return n;
        });
      } else if (payload.kind === 'archived-task') {
        // Recover a deleted task: un-archive it (reactivate) and schedule it on the drop date.
        await updatePlanningTask(payload.taskId, {
          date_deadline: target.dateKey,
          x_studio_installateur: installerIds,
          reactivate: true,
        });
        setArchived((prev) => prev.filter((a) => a.id !== payload.taskId));
      } else {
        const res = await scheduleFase({ faseId: payload.faseId, projectId, installerIds, date: target.dateKey });
        // Dropping "+ Nieuwe taak" on the unassigned row when a task of this fase
        // already exists that day appends nothing (no installer to add) — say so
        // rather than silently doing nothing.
        if (res.appended && installerIds.length === 0) {
          usePlanningStore.setState({ error: 'Er staat die dag al een taak van deze fase.' });
        }
      }
      // Refresh the in-place board so the placed task appears immediately, then sync
      // the dashboard detail (tray + fase status) via onScheduled.
      await usePlanningStore.getState().fetchPlanning(undefined, { silent: true });
      onScheduled();
    } catch {
      usePlanningStore.setState({ error: 'Inplannen mislukt' });
    }
  }

  const weekLabel = `Week ${weekData.weekNumber} · ${fmt(weekData.days[0])}–${fmt(weekData.days[weekData.days.length - 1])}`;

  return (
    <div className="rounded-lg border border-td-blue/40 bg-white">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-extrabold text-td-blue">{readOnly ? 'Planning' : 'Inplannen'}</span>
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek project…"
              className="w-40 rounded-lg border border-gray-200 px-2.5 py-1.5 pr-6 text-xs focus:border-td-blue focus:outline-none sm:w-52"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Filter wissen"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-td-muted hover:text-td-navy"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center overflow-hidden rounded-lg border border-gray-200">
            <button
              type="button"
              aria-label="Vorige week"
              onClick={() => changeWeek(-1)}
              className="px-3 py-1.5 text-td-navy hover:bg-gray-100"
            >
              ‹
            </button>
            <span className="border-x border-gray-200 px-3 py-1.5 text-xs font-semibold text-td-navy">{weekLabel}</span>
            <button
              type="button"
              aria-label="Volgende week"
              onClick={() => changeWeek(1)}
              className="px-3 py-1.5 text-td-navy hover:bg-gray-100"
            >
              ›
            </button>
          </div>
          <button
            type="button"
            onClick={() => navigate(`/planning?q=${encodeURIComponent(projectName)}&week=${formatDateKey(weekData.days[0])}&pid=${projectId}`)}
            className="rounded-lg border border-td-blue px-3 py-1.5 text-sm font-semibold text-td-blue hover:bg-td-blue/10"
          >
            Bekijk in planning
          </button>
        </div>
      </div>

      {/* Body */}
      {/* The grid stays mounted; a loading overlay covers it while fetching so an
          empty / not-yet-loaded grid never reads as "no planning". */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/65">
            <div className="mt-12 flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 shadow-md">
              <span
                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-td-blue/30 border-t-td-blue"
                role="status"
              >
                <span className="sr-only">Laden…</span>
              </span>
              <span className="text-sm font-semibold text-td-navy">Planning laden…</span>
            </div>
          </div>
        )}
        <div className="py-3">
          <PlanningGrid
            flatRows={orderedFlatRows}
            days={weekData.days}
            feestdagDates={feestdagDates}
            collapsedGroups={collapsedGroups}
            onToggleCollapse={toggleGroup}
            allowRowReorder={false}
            disableCellInteractions
            renderAside={
              readOnly ? undefined : (
                <InplannenTray
                  faseId={faseId}
                  faseName={faseName}
                  unplannedTasks={visibleUnplanned}
                  archivedTasks={faseArchived}
                />
              )
            }
            onTrayDrop={readOnly ? undefined : handleTrayDrop}
            onCardClick={(odooTask) => {
              const full = projectTasks.find((t) => t.id === odooTask.id);
              if (full) { setPopupTask(full); return; }
              // Cross-project card: the description/documenten popup is sourced from THIS
              // project's detail, so there's nothing to show — jump to that card's project
              // instead of silently doing nothing.
              if (Array.isArray(odooTask.project_id)) {
                navigate(`/project/${odooTask.project_id[0]}?task=${odooTask.id}`);
              }
            }}
            onTaskMoved={handleBoardChanged}
          />
        </div>
      </div>

      {error && (
        <div className="border-t border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</div>
      )}

      {popupTask && (
        <TaskDetailPopup
          projectId={projectId}
          faseId={Array.isArray(popupTask.parent_id) ? popupTask.parent_id[0] : null}
          task={popupTask}
          installerNames={installerNames}
          onClose={() => setPopupTask(null)}
        />
      )}
    </div>
  );
}
